<template>
	<slot name="trigger" :open-drawer="openDrawer" :close-drawer="closeDrawer" :toggle-drawer="toggleDrawer"></slot>
	<Teleport to="#app">
		<div class="fixed inset-0 z-0 bg-brown-800" v-if="drawerOpen"></div>
		<Transition
			enter-from-class="translate-x-80"
			leave-to-class="translate-x-80"
			enter-active-class="transition-transform duration-700 ease-in-out"
			leave-active-class="transition-transform duration-700 ease-in-out"
		>
			<div ref="drawer" id="nav-drawer" class="fixed z-10 inset-y-0 right-0 w-full min-[33.75rem]:max-w-[25rem] sm:max-w-[31.25rem] focus-visible:outline-none after:absolute after:inset-0 after:pointer-events-none after:outline-0 focus-visible:after:outline-2 after:outline after:outline-white/15 after:-outline-offset-4" v-cloak v-if="drawerOpen" v-click-outside="closeDrawer" @click.capture="handleClick" tabindex="-1">
				<slot name="content" :open-drawer="openDrawer" :close-drawer="closeDrawer" :toggle-drawer="toggleDrawer"></slot>
			</div>
		</Transition>
	</Teleport>
</template>

<script setup>
import { ref, computed, watch, onUnmounted, nextTick } from "vue";
import { lock, unlock } from "@js/body-scroll-lock.js";
import { create, activate, deactivate } from "@js/focus-trap.js";
import { store, mutations } from "@js/vue/store.js";

const drawer = ref(null);
const focusTrap = ref(null);
const drawerOpen = computed(() => store.navDrawerOpen);

function openDrawer() {
	mutations.openNavDrawer();
}

function closeDrawer() {
	mutations.closeNavDrawer();
}

function toggleDrawer() {
	mutations.toggleNavDrawer();
}

watch(drawerOpen, async (newValue) => {
	if (newValue) {
		lock();
		await nextTick();
		focusTrap.value = create(drawer.value);
		activate(focusTrap.value);
	} else {
		unlock();
		if (focusTrap.value) {
			deactivate(focusTrap.value);
		}
	}
}, { immediate: true });

function handleKeydown(e) {
	if (e.key === 'Escape' && drawerOpen.value) {
		closeDrawer();
	}
}

window.addEventListener('keydown', handleKeydown);

onUnmounted(() => {
	window.removeEventListener('keydown', handleKeydown);
});

const handleClick = async (e) => {
	// Check if this is an artificial click event
	if (e.isTrusted === false) {
		return // Skip handling programmatic clicks
	}

	// Stop the original click event
	e.stopPropagation()
	e.preventDefault()

	// Check if the click event is on a link or button
	const isClickable = e.target.closest('a, button');
	// Check if the link is an anchor link
	const isAnchorLink = isClickable && isClickable.tagName === 'A' && isClickable.getAttribute('href').startsWith('#');

	// If the link is an anchor link, close the drawer
	if (isAnchorLink) {
		closeDrawer()
		// Wait for drawer to partly close before proceeding
		await new Promise(resolve => setTimeout(resolve, 300));
	}

	// If the link is not an anchor link, simulate a click event on it
	if (isClickable) {
		isClickable.click()
	}

}
</script>
